/**
 * Event bridge — wires the engine's `plan()` to gantt-side event firing.
 *
 * Implements the D-01 two-phase plan flow + D-17 event order + D-18
 * per-variant `ScheduleReason` → event-arg mapping.
 *
 * The engine itself never calls gantt back; it returns a pure `PlanResult`.
 * This module:
 *   1. Fires `onBeforeAutoSchedule(id)` (cancelable).
 *   2. Runs `plan_1` against the current snapshot.
 *   3. Short-circuits on cycles (matches v1's abort path with the
 *      `onAutoScheduleCircularLink` notification).
 *   4. Iterates `plan_1.order` and fires per-task `onBeforeTaskAutoSchedule`
 *      with D-18-derived args; collects cancellations.
 *   5. If any cancellations, re-builds the snapshot with `cancelledTaskIds`
 *      forcing those tasks to `autoScheduling: false` and re-plans once.
 *   6. Writes the diff inside `gantt.batchUpdate` and fires
 *      `onAfterTaskAutoSchedule` per task.
 *   7. Fires `onAutoScheduleConflict` for each non-cycle conflict (new in v2
 *      per `04-` §18) and `onAutoScheduleNoConverge` when the loop
 *      exhausted iterations.
 *   8. Fires the final `onAfterAutoSchedule(id, updatedTaskIds)`.
 *
 * Re-entrance is guarded via `gantt._autoscheduling_in_progress` so listener
 * callbacks that themselves trigger `autoSchedule` (e.g., `onAfterTaskUpdate`
 * via the Day 11 triggers) don't recurse — matches v1's behavior.
 */

import { plan } from "../core/api";
import { plan_unmemoized } from "../core/planner/plan";
import { applyPlan } from "./applier";
import { buildSnapshot } from "./snapshot_builder";
import { getOrBuildSnapshot } from "./gantt_adapter";
import type {
  Conflict,
  PlanResult,
  ScheduleReason,
  Snapshot,
  TaskID
} from "../core/types";
import { rawTaskId, toPublicConflict, toPublicScheduleReason } from "../core/ids";
import type { GanttSnapshotBackend } from "./snapshot_builder";

// ─────────────────────────────────────────────────────────────────────────────
// Public surface
// ─────────────────────────────────────────────────────────────────────────────

export interface AutoScheduleResult {
  /** Tasks whose dates actually changed (matches v1's `updatedTasks`). */
  readonly updatedTaskIds: readonly TaskID[];
  readonly converged: boolean;
  readonly conflicts: readonly Conflict[];
  /** Tasks whose `onBeforeTaskAutoSchedule` listener returned `false`. */
  readonly cancelled: ReadonlySet<TaskID>;
}

/**
 * Structural gantt subset consumed by the event bridge. Extends the
 * snapshot-builder backend with the additional surfaces this module touches
 * (event/batch/write). The adapter imports nothing from `gantt` — the
 * boundary is structural per `04-` §12 and D-33.
 */
export interface GanttEventBridgeBackend extends GanttSnapshotBackend {
  callEvent: (name: string, args: unknown[]) => unknown;
  batchUpdate: (payload: () => void, suppressRepaint?: boolean) => void;
  updateTask: (taskId: TaskID) => void;
  isTaskExists: (taskId: TaskID) => boolean;
  getLink: (linkId: TaskID) => unknown;
  /** Calendar-aware end-date derivation retained on the structural backend for v1 parity tests. */
  calculateEndDate: (task: { start_date?: Date; end_date?: Date; duration?: number }) => Date;
  /**
   * Re-entrance guard flag — v1 sets this on `_autoSchedule` entry and
   * clears on exit. UI handlers (link change, drag end) check it before
   * triggering nested `autoSchedule` calls. Day 10 preserves the contract;
   * Day 11's `auto_schedule_triggers.ts` reads it.
   */
  _autoscheduling_in_progress?: boolean;
}

/**
 * Run the v2 auto-scheduling pipeline end-to-end against a live gantt
 * instance.
 *
 * @returns `null` when `onBeforeAutoSchedule` returned `false` or when a
 *          re-entrance guard prevents execution. Otherwise an
 *          `AutoScheduleResult` describing the outcome.
 */
export function autoScheduleViaEngine(
  gantt: GanttEventBridgeBackend,
  id?: TaskID,
  inclusive: boolean = true
): AutoScheduleResult | null {
  // (1) Re-entrance guard. Listeners that re-enter must no-op so cascading
  // events don't recurse (v1 behavior — set on `_autoSchedule` entry).
  if (gantt._autoscheduling_in_progress) return null;
  gantt._autoscheduling_in_progress = true;

  try {
    // (2) onBeforeAutoSchedule — cancelable. Returning explicit `false`
    // aborts the whole call (no onAfterAutoSchedule fires either, matching
    // v1's `if (callEvent(...) === false) return;` at auto_scheduling.js:108).
    if (gantt.callEvent("onBeforeAutoSchedule", [id]) === false) {
      return null;
    }

    // (3) First snapshot + plan. The snapshot cache (RevisionTracker) means
    // repeat calls within the same gantt revision reuse the same Snapshot
    // instance, and `plan()` memoizes per (snapshot, rootTaskId, inclusive).
    const snapshot_1 = getOrBuildSnapshot(gantt);
    const plan_1 = plan(snapshot_1, id, inclusive);

    // (4) Cycle short-circuit. v1 fires onAutoScheduleCircularLink at
    // auto_scheduling.js:122 before any planning attempt; v2 emits the same
    // event from the conflict list and skips per-task events entirely.
    const cycleConflicts = plan_1.conflicts.filter(c => c.kind === "cycle");
    if (cycleConflicts.length > 0) {
      const publicConflicts = plan_1.conflicts.map(c => toPublicConflict(snapshot_1, c));
      gantt.callEvent("onAutoScheduleCircularLink", [publicConflicts.filter(c => c.kind === "cycle")]);
      gantt.callEvent("onAfterAutoSchedule", [id, []]);
      return {
        updatedTaskIds: [],
        converged: plan_1.converged,
        conflicts: publicConflicts,
        cancelled: new Set<TaskID>()
      };
    }

    // (5) Per-task onBeforeTaskAutoSchedule pass. Iterate in plan order so
    // listeners see the natural topological progression (D-17). After each
    // cancellation, re-plan with the cancellation injected and continue.
    // This mirrors v1's sequential scheduling (`planner.iterateTasks`) where
    // cancelling task X via `processResolvedDate` leaves `plansHash[X] = null`
    // and downstream tasks reading `getPredecessorEndDate(X)` see X's CURRENT
    // (unchanged) end_date — which often equals the downstream task's current
    // start_date, so its plan doesn't change and no further event fires for
    // it. The naive v2 approach (fire all events from plan_1, then re-plan)
    // over-fired for downstream-of-cancelled tasks (test 067 GS-1598).
    //
    // Skip summary tasks — v1's `getConstraints` filters them at
    // `constraints.ts:210`, so they never enter the iteration loop. v2 keeps
    // summaries as scheduling nodes under `moveProjects:true` for the block-
    // translation model (D-08), but firing the public event for them breaks
    // v1 parity. The summary's translation still applies — only the listener
    // hook is suppressed.
    const cancelled = new Set<TaskID>();
    const firedTasks = new Set<TaskID>();
    let currentSnapshot: Snapshot = snapshot_1;
    let currentPlan: PlanResult = plan_1;
    // Bound the outer loop defensively — N+1 cancellations would require N+1
    // re-plans. The number of cancellation candidates is finite (a task can
    // be cancelled at most once), so the loop terminates without this guard,
    // but the bound keeps a runaway listener from spinning.
    const maxOuterIterations = currentSnapshot.tasks.size + 1;
    for (let outerIter = 0; outerIter < maxOuterIterations; outerIter++) {
      let newCancellation = false;
      for (const taskId of currentPlan.order) {
        const scheduled = currentPlan.plans.get(taskId);
        if (!scheduled) continue; // task didn't change in this plan
        const publicTaskId = rawTaskId(currentSnapshot, taskId);
        if (!gantt.isTaskExists(publicTaskId)) continue;
        if (cancelled.has(publicTaskId)) continue;
        if (firedTasks.has(publicTaskId)) continue;
        // v1 parity: skip type:"project" / summary tasks (see comment above).
        const taskSnap = currentSnapshot.tasks.get(taskId);
        if (taskSnap?.kind === "summary") continue;
        const publicReason = toPublicScheduleReason(currentSnapshot, scheduled.reason);
        const [task, newDate, link, reasonTask] =
          deriveEventArgs(publicTaskId, publicReason, scheduled.start, gantt);
        firedTasks.add(publicTaskId);
        const ok = gantt.callEvent("onBeforeTaskAutoSchedule", [task, newDate, link, reasonTask]);
        if (ok === false) {
          cancelled.add(publicTaskId);
          newCancellation = true;
          // Re-plan immediately so subsequent iterations see the cancellation
          // propagated through the dependency graph. Re-evaluating
          // `currentPlan.order` inside the for-loop is safe because we restart
          // the outer `for` after `break`.
          currentSnapshot = buildSnapshot(gantt, { cancelledTaskIds: cancelled });
          currentPlan = plan_unmemoized(currentSnapshot, id, inclusive);
          break;
        }
      }
      if (!newCancellation) break;
    }

    // (6) `currentPlan` is now the final plan with all cancellations applied.
    const finalResult: PlanResult = currentPlan;
    const snapshot_final: Snapshot = currentSnapshot;

    // (7) Build the diff and apply. v1 parity (`planner.applyProjectPlan` +
    // `updateParentsAndCallEvents`): the order of public events around the
    // mutation step is documented and observable by user listeners (test
    // `11_auto_scheduling/72_order_of_auto_scheduling_events_*`). v1 first
    // mutates each task's `start_date` / `end_date` and fires the per-task
    // `onAfterTaskAutoSchedule` FOR ALL tasks; only after that does it run a
    // batchUpdate that calls `gantt.updateTask(id)` for each task (which
    // fires onBeforeTaskUpdate / onAfterTaskUpdate). Mirror that order — the
    // write-back step and the `onAfterTaskAutoSchedule` per task happen
    // OUTSIDE the batch, and the `updateTask` calls happen INSIDE.
    // v1 parity: summary tasks never receive write-backs from auto-scheduling.
    // v1's `getConstraints` filters summaries out of the plan entirely, so its
    // `applyProjectPlan` neither mutates them, nor fires
    // `onAfterTaskAutoSchedule` for them, nor includes them in `updatedTasks`
    // (gantt core re-derives summary dates from children via
    // `resetProjectDates` on the children's `updateTask` anyway). v2 keeps
    // summaries in the plan for the D-08 block model, but writing them back
    // here is observably different from v1: the extra `updateTask(summary)`
    // feeds a summary command into the undo extension's current batch action
    // WITHOUT the matching `onBeforeTaskAutoSchedule` store (suppressed for
    // summaries above), so the command's oldValue comes from the monitor's
    // stale initial-task cache. Undoing such an action re-installs a stale
    // `constraint_type`/`constraint_date` snapshot onto the summary, and the
    // next v2 pass honors the summary constraint (D-26) by translating the
    // whole block — retest 19_undo/010 (multi-drag + undo) caught this as
    // "undo didn't restore dates".
    const mutations = applyPlan(snapshot_final, finalResult).filter(
      mut => snapshot_final.tasks.get(mut.normalizedTaskId)?.kind !== "summary"
    );
    const updatedTaskIds: TaskID[] = [];
    const beforeFirstRepaint =
      ((gantt as unknown as { _afterRenderBeforeFirstRepaint?: boolean })
        ._afterRenderBeforeFirstRepaint === true);

    // Phase 1 — write live dates and fire `onAfterTaskAutoSchedule` per task,
    // in plan order. No `updateTask` yet.
    for (const mut of mutations) {
      if (!gantt.isTaskExists(mut.taskId)) continue;
      const liveTask = gantt.getTask(mut.taskId) as { start_date?: Date; end_date?: Date; duration?: number };
      liveTask.start_date = mut.next.start;
      liveTask.end_date = gantt.calculateEndDate(liveTask);
      const [task, newDate, link, reasonTask] =
        deriveEventArgs(mut.taskId, mut.reason, mut.next.start, gantt);
      gantt.callEvent("onAfterTaskAutoSchedule", [task, newDate, link, reasonTask]);
      updatedTaskIds.push(mut.taskId);
    }

    // Phase 2 — batched updateTask so dependent re-renders (parents, layers)
    // see the consistent end-state.
    gantt.batchUpdate(() => {
      for (const mut of mutations) {
        if (!gantt.isTaskExists(mut.taskId)) continue;
        gantt.updateTask(mut.taskId);
      }
    }, beforeFirstRepaint);

    // Test-debugging side-channel: Day 14's `30_auto_scheduling_convergence.ts`
    // reads `gantt._v2_lastIterations` to assert the fixed-point loop bound.
    // Documented as a test hook, NOT public API; Phase E removes both this
    // assignment and any test references.
    (gantt as unknown as { _v2_lastIterations?: number })._v2_lastIterations =
      finalResult.iterations;

    // (8) New v2 events per `04-` §18. Conflict events fire per
    // non-cycle conflict (cycle events fired earlier in the short-circuit
    // path; we'd never reach here with cycles in the conflict list).
    const publicConflicts = finalResult.conflicts.map(c => toPublicConflict(snapshot_final, c));
    for (const c of publicConflicts) {
      if (c.kind === "cycle") continue;
      gantt.callEvent("onAutoScheduleConflict", [c]);
    }
    if (!finalResult.converged) {
      gantt.callEvent("onAutoScheduleNoConverge", [finalResult]);
    }

    // (9) Final wrap-up event. v1 fires this at auto_scheduling.js:143.
    gantt.callEvent("onAfterAutoSchedule", [id, updatedTaskIds]);

    return {
      updatedTaskIds,
      converged: finalResult.converged,
      conflicts: publicConflicts,
      cancelled
    };
  } finally {
    gantt._autoscheduling_in_progress = false;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// D-18 ScheduleReason → event arg mapping
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Per `04-` §18 (D-18 row) and `05-` §A.7 lines 418-423.
 *
 * | reason.kind           | link arg              | reasonTask arg          |
 * |-----------------------|-----------------------|--------------------------|
 * | "asap"                | gantt.getLink(linkId) | gantt.getTask(sourceId)  |
 * | "alap"                | gantt.getLink(linkId) | gantt.getTask(targetId)  |
 * | "constraint"          | null  (BREAKING)      | null  (BREAKING)         |
 * | "preferred"           | null  (BREAKING)      | null  (BREAKING)         |
 * | "summary-translation" | null                  | gantt.getTask(summaryId) |
 *
 * The BREAKING-from-v1 cases pass `null` for both `link` and `reasonTask`.
 * Listeners must null-check (documented in `04-` §18 migration table).
 */
export function deriveEventArgs(
  taskId: TaskID,
  reason: ScheduleReason,
  newDate: Date,
  gantt: GanttEventBridgeBackend
): readonly [unknown, Date, unknown, unknown] {
  const task = gantt.getTask(taskId);
  switch (reason.kind) {
    case "asap":
      return [task, newDate, gantt.getLink(reason.linkId), gantt.getTask(reason.sourceId)];
    case "alap":
      return [task, newDate, gantt.getLink(reason.linkId), gantt.getTask(reason.targetId)];
    case "constraint":
      return [task, newDate, null, null];
    case "preferred":
      return [task, newDate, null, null];
    case "summary-translation":
      return [task, newDate, null, gantt.getTask(reason.summaryId)];
    default: {
      // Exhaustiveness check — unreachable at runtime.
      const _exhaustive: never = reason;
      void _exhaustive;
      return [task, newDate, null, null];
    }
  }
}
